import React, { useState, useEffect } from 'react';
import { TextField, Container, Typography, Paper, Alert } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../services/api';
import { StyledButton } from '../components/StyledComponents';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

function ResetPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setIsSubmitting(true);

        try {
            await resetPassword(token, password);
            setSuccess(true);
            // Clear the form
            setPassword('');
            setConfirmPassword('');
            // Redirect to login after 3 seconds
            setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
            console.error('Password reset failed', error);
            if (error.response?.status === 400) {
                setError('Invalid or expired reset token. Please request a new password reset.');
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return null;
    }

    return (
        <Container maxWidth="xs" style={{ marginTop: '50px' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
                Reset Password
            </Typography>
            <StyledPaper elevation={3}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success ? (
                    <Alert severity="success">
                        Password successfully reset! Redirecting to login...
                    </Alert>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Please enter your new password.
                        </Typography>
                        <StyledTextField
                            variant="outlined"
                            required
                            fullWidth
                            label="New Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <StyledTextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Confirm New Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <StyledButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Resetting...' : 'Reset Password'}
                        </StyledButton>
                    </form>
                )}
            </StyledPaper>
        </Container>
    );
}

export default ResetPassword;
